import { isArray } from '../array.js';
import type { ToolDefinition, ToolConfig, HomeRenovationToolId } from '../../types/tool-registry.js';
import type { CoreMetadata, HgsConfig, HgsCrossing, HgsVehicleClass, HomeRenovationInput } from '../../types/calculation-core.js';
function configHasKind<K extends ToolConfig['kind']>(config: ToolConfig, kind: K): config is Extract<ToolConfig, {
    kind: K;
}> { return config.kind === kind; }
export * from './date-education.js';
export * from './finance-business.js';
function metadataFor(tool: ToolDefinition): CoreMetadata {
    return {
        calculatorId: tool.toolId,
        version: tool.configVersion,
        effectiveFrom: tool.effectiveFrom,
        ...(tool.effectiveTo ? { effectiveTo: tool.effectiveTo } : {}),
        lastVerifiedAt: tool.lastReviewed,
        sources: tool.sources.map((source) => ({
            id: source.sourceId,
            title: source.title,
            publisher: source.publisher,
            url: source.url,
            verifiedAt: source.reviewedOn,
        })),
        notes: tool.content.limitations,
    };
}
function requireConfig<K extends ToolConfig["kind"]>(tool: ToolDefinition, expectedKind: K): Extract<ToolConfig, {
    kind: K;
}> {
    const config = tool.config;
    if (!configHasKind(config, expectedKind)) {
        throw new Error(`${tool.toolId} için beklenmeyen konfigürasyon: ${config.kind}`);
    }
    return config;
}
export function isToolEffectiveForDate(tool: ToolDefinition, currentDate: string) {
    if (tool.classification === 'timeless')
        return true;
    const insideEffectiveWindow = currentDate >= tool.effectiveFrom &&
        (tool.effectiveTo === null || currentDate <= tool.effectiveTo);
    if (!insideEffectiveWindow)
        return false;
    const reviewAfter = tool.updateCadence.reviewAfter;
    return ['hgs-toll', 'relative-grade', 'grade-average'].includes(tool.toolId) || reviewAfter === null || currentDate < reviewAfter;
}
export function toolAvailabilityNoticeForDate(tool: ToolDefinition, currentDate: string) {
    if (isToolEffectiveForDate(tool, currentDate))
        return null;
    if (currentDate < tool.effectiveFrom) {
        return `Bu aracın doğrulanmış verisi ${tool.effectiveFrom} tarihinde yürürlüğe girecek. O tarihe kadar hesaplama kapalıdır.`;
    }
    if (tool.effectiveTo !== null && currentDate > tool.effectiveTo) {
        return `Bu aracın doğrulanmış veri dönemi ${tool.effectiveTo} tarihinde sona erdi. Yeni doğrulanmış veri gelene kadar hesaplama kapalıdır.`;
    }
    if (tool.updateCadence.reviewAfter !== null &&
        currentDate >= tool.updateCadence.reviewAfter) {
        return `Bu aracın planlı resmî kaynak incelemesi ${tool.updateCadence.reviewAfter} tarihinde geldi. Yeni insan onaylı kayıt gelene kadar hesaplama kapalıdır.`;
    }
    return `Bu aracın doğrulanmış veri dönemi ${tool.effectiveTo ?? tool.effectiveFrom} tarihinde sona erdi. Yeni doğrulanmış veri gelene kadar hesaplama kapalıdır.`;
}
export function assertToolEffectiveForDate(tool: ToolDefinition, currentDate: string) {
    const notice = toolAvailabilityNoticeForDate(tool, currentDate);
    if (notice)
        throw new Error(notice);
}
export function percentageConfigFor(tool: ToolDefinition) {
    requireConfig(tool, 'percentage-presentation-v1');
    return {
        metadata: metadataFor(tool),
        maxAbsoluteInput: Number.MAX_SAFE_INTEGER,
    };
}
export function unitConverterConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'unit-converter-v1');
    return {
        metadata: metadataFor(tool),
        converterId: config.converterId,
        strategy: config.strategy,
        allowNegative: config.allowNegative,
        maxAbsoluteInput: config.maxAbsoluteInput,
        units: config.units.map((unit) => ({
            id: unit.id,
            label: unit.label,
            symbol: unit.symbol,
            factor: unit.factor,
        })),
    };
}
function staticMathBaseConfig(tool: ToolDefinition, maxAbsoluteInput: number) {
    return {
        metadata: metadataFor(tool),
        maxAbsoluteInput,
    };
}
export function geometryConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'geometry-static-v1');
    return staticMathBaseConfig(tool, config.maxAbsoluteInput);
}
export function primeNumberConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'prime-number-static-v1');
    return {
        ...staticMathBaseConfig(tool, config.maxAbsoluteInput),
        maxInteger: config.maxInteger,
    };
}
export function gcdLcmConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'gcd-lcm-static-v1');
    return {
        ...staticMathBaseConfig(tool, config.maxAbsoluteInput),
        maxInteger: config.maxInteger,
    };
}
export function powerRootConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'power-root-static-v2');
    return {
        ...staticMathBaseConfig(tool, config.maxAbsoluteInput),
        maxN: config.maxN,
    };
}
export function quadraticEquationConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'quadratic-equation-static-v1');
    return staticMathBaseConfig(tool, config.maxAbsoluteInput);
}
export function fractionConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'fraction-static-v1');
    return {
        ...staticMathBaseConfig(tool, config.maxAbsoluteInput),
        maxAbsoluteInteger: config.maxAbsoluteInteger,
    };
}
export function ratioProportionConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'ratio-proportion-static-v1');
    return staticMathBaseConfig(tool, config.maxAbsoluteInput);
}
export function pythagoreanConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'pythagorean-static-v1');
    return staticMathBaseConfig(tool, config.maxAbsoluteInput);
}
export function homeRenovationConfigFor(tool: ToolDefinition) {
    const config = tool.toolId === 'curtain-fabric'
        ? requireConfig(tool, 'curtain-fabric-v2')
        : requireConfig(tool, 'home-renovation-v1');
    if (config.toolId !== tool.toolId ||
        config.metadata.calculatorId !== tool.toolId) {
        throw new Error(`${tool.toolId} ev/tadilat araç kimliği registry config'iyle eşleşmiyor.`);
    }
    return {
        metadata: metadataFor(tool),
        maxAbsoluteInput: config.maxAbsoluteInput,
    };
}
const homeRenovationInputContracts: Readonly<Record<HomeRenovationToolId, {
    readonly fields: readonly string[];
    readonly required: readonly string[];
    readonly visibleWhen?: Readonly<Record<string, {
        readonly fieldId: string;
        readonly equals: string;
    }>>;
}>> = {
    'alan-fire': {
        fields: ['netAmount', 'wastePercent', 'unit', 'unitPrice'],
        required: ['netAmount'],
    },
    'alci-siva-macun': {
        fields: ['areaM2', 'thicknessMm', 'consumptionKgPerM2Mm', 'wastePercent', 'bagKg', 'bagPrice'],
        required: ['areaM2', 'thicknessMm', 'consumptionKgPerM2Mm'],
    },
    boya: {
        fields: ['roomWidthM', 'roomLengthM', 'wallHeightM', 'openingsAreaM2', 'coatCount', 'coverageM2PerLitre', 'wastePercent', 'bucketLitres', 'includeCeiling', 'litrePrice'],
        required: ['roomWidthM', 'roomLengthM', 'wallHeightM'],
    },
    'derz-dolgu': {
        fields: ['areaM2', 'tileWidthCm', 'tileHeightCm', 'jointWidthMm', 'jointDepthMm', 'densityCoefficient', 'wastePercent', 'bagKg'],
        required: ['areaM2', 'tileWidthCm', 'tileHeightCm', 'jointWidthMm', 'jointDepthMm'],
    },
    'duvar-kagidi-rulo': {
        fields: ['wallWidthM', 'wallHeightM', 'rollWidthCm', 'rollLengthM', 'patternRepeatCm', 'wastePercent', 'rollPrice'],
        required: ['wallWidthM', 'wallHeightM', 'rollWidthCm', 'rollLengthM'],
    },
    'fayans-seramik': {
        fields: ['areaWidthM', 'areaLengthM', 'tileWidthCm', 'tileHeightCm', 'tilesPerBox', 'wastePercent', 'boxPrice'],
        required: ['areaWidthM', 'areaLengthM', 'tileWidthCm', 'tileHeightCm', 'tilesPerBox'],
    },
    'curtain-fabric': {
        fields: [
            'mode', 'windowWidthCm', 'tulleFullness', 'sideAllowanceCm', 'tulleHemAllowanceCm',
            'panelCount', 'finishedPanelWidthCm', 'draperyFullness', 'draperyHemAllowanceCm',
            'curtainHeightCm', 'metrePrice',
        ],
        required: ['mode', 'windowWidthCm', 'finishedPanelWidthCm', 'curtainHeightCm'],
        visibleWhen: {
            windowWidthCm: { fieldId: 'mode', equals: 'tul' },
            tulleFullness: { fieldId: 'mode', equals: 'tul' },
            sideAllowanceCm: { fieldId: 'mode', equals: 'tul' },
            tulleHemAllowanceCm: { fieldId: 'mode', equals: 'tul' },
            panelCount: { fieldId: 'mode', equals: 'fon' },
            finishedPanelWidthCm: { fieldId: 'mode', equals: 'fon' },
            draperyFullness: { fieldId: 'mode', equals: 'fon' },
            draperyHemAllowanceCm: { fieldId: 'mode', equals: 'fon' },
        },
    },
    'hali-metrekare': {
        fields: ['widthCm', 'lengthCm', 'pricePerM2'],
        required: ['widthCm', 'lengthCm'],
    },
    'kutu-paket-sayisi': {
        fields: ['netNeedM2', 'packageAreaM2', 'wastePercent', 'packagePrice'],
        required: ['netNeedM2', 'packageAreaM2'],
    },
    'laminat-parke': {
        fields: ['roomWidthM', 'roomLengthM', 'packageAreaM2', 'wastePercent', 'parquetPricePerM2', 'includeSkirting'],
        required: ['roomWidthM', 'roomLengthM', 'packageAreaM2'],
    },
    'oda-metrekare': {
        fields: ['roomWidthM', 'roomLengthM'],
        required: ['roomWidthM', 'roomLengthM'],
    },
    'supurgelik-metre': {
        fields: ['roomWidthM', 'roomLengthM', 'doorCount', 'doorWidthCm', 'wastePercent', 'pieceLengthM', 'piecePrice'],
        required: ['roomWidthM', 'roomLengthM'],
    },
    'zebra-stor-perde-olcu': {
        fields: ['windowWidthCm', 'windowHeightCm', 'mechanismAllowanceCm', 'minimumBillableAreaM2', 'pricePerM2'],
        required: ['windowWidthCm', 'windowHeightCm'],
    },
};
export function homeRenovationInputFor(tool: ToolDefinition, userInput: Readonly<Record<string, unknown>>): HomeRenovationInput {
    const config = tool.toolId === 'curtain-fabric'
        ? requireConfig(tool, 'curtain-fabric-v2')
        : requireConfig(tool, 'home-renovation-v1');
    homeRenovationConfigFor(tool);
    const contract = homeRenovationInputContracts[config.toolId];
    const contractVisibleWhen = contract.visibleWhen ?? {};
    const schemaProjection = tool.inputSchema.fields.map((field) => ({
        id: field.id,
        required: field.required,
        visibleWhen: field.visibleWhen,
    }));
    const contractProjection = contract.fields.map((id) => ({
        id,
        required: contract.required.includes(id),
        visibleWhen: contractVisibleWhen[id] ?? null,
    }));
    if (JSON.stringify(schemaProjection) !== JSON.stringify(contractProjection)) {
        throw new Error(`${tool.toolId} ev/tadilat input şeması kapalı alan sözleşmesiyle eşleşmiyor.`);
    }
    if (!userInput || typeof userInput !== 'object' || isArray(userInput)) {
        throw new Error(`${tool.toolId} ev/tadilat kullanıcı girdisi nesne olmalıdır.`);
    }
    for (const field of contract.required) {
        const visibility = contractVisibleWhen[field];
        if (visibility && userInput[visibility.fieldId] !== visibility.equals)
            continue;
        if (!Object.prototype.hasOwnProperty.call(userInput, field) || userInput[field] === undefined || userInput[field] === null) {
            throw new Error(`${tool.toolId}.${field} zorunlu kullanıcı alanı eksik.`);
        }
    }
    const fields: Record<string, unknown> = {};
    for (const field of contract.fields) {
        const visibility = contractVisibleWhen[field];
        if (visibility && userInput[visibility.fieldId] !== visibility.equals)
            continue;
        if (Object.prototype.hasOwnProperty.call(userInput, field))
            fields[field] = userInput[field];
    }
    return { ...fields, toolId: config.toolId };
}
export function salaryConfigFor(tool: ToolDefinition) {
    const config = requireConfig(tool, 'salary-annual-rules-v1');
    const amountField = tool.inputSchema.fields.find((field) => field.id === 'monthlyAmount');
    if (config.monthsPerYear !== 12) {
        throw new Error('Maaş motoru bu sürümde yalnız 12 aylık takvimi destekler.');
    }
    return {
        metadata: metadataFor(tool),
        taxBrackets: config.incomeTaxBrackets,
        grossMinimumWage: config.minimumGrossWage,
        employeeSgkRate: config.socialSecurityEmployeeRate,
        employeeUnemploymentRate: config.unemploymentEmployeeRate,
        stampTaxRate: config.stampTaxRate,
        sgkCeilingMultiplier: config.socialSecurityCeilingMultiplier,
        maxMonthlyAmount: amountField?.maximum ?? Number.MAX_SAFE_INTEGER / 12,
        inverse: {
            initialGrossMultiplier: config.netToGrossIteration.initialMultiplier,
            adjustmentFactor: config.netToGrossIteration.correctionFactor,
            tolerance: config.netToGrossIteration.tolerance,
            maxIterations: config.netToGrossIteration.maxIterations,
        },
    };
}
function feeTable(prices: Readonly<Partial<Record<HgsVehicleClass, number | null>>>) {
    return [
        prices['class-1'] ?? null,
        prices['class-2'] ?? null,
        prices['class-3'] ?? null,
        prices['class-4'] ?? null,
        prices['class-5'] ?? null,
        prices['class-6'] ?? null,
    ];
}
function crossingFor(config: Extract<ToolConfig, {
    kind: "hgs-fixed-tariffs-v1";
}>, crossingId: HgsCrossing): HgsConfig["crossings"][HgsCrossing] {
    const crossing = config.crossings.find((item) => item.id === crossingId);
    if (!crossing) {
        throw new Error(`HGS tarifesinde ${crossingId} kaydı eksik.`);
    }
    return {
        name: crossing.label,
        standardFees: feeTable(crossing.pricesByVehicleClass),
        ...(crossing.nightPricesByVehicleClass
            ? {
                nightFees: feeTable(crossing.nightPricesByVehicleClass),
                nightWindow: {
                    startInclusive: config.nightWindow.start,
                    endExclusive: config.nightWindow.end,
                    timeZone: 'Europe/Istanbul',
                },
            }
            : {}),
    };
}
export function hgsCrossingIsEffective(tool: ToolDefinition, crossingId: HgsCrossing, currentDate: string) {
    const config = requireConfig(tool, 'hgs-fixed-tariffs-v1');
    const crossing = config.crossings.find((item) => item.id === crossingId);
    return crossing !== undefined && crossing.effectiveFrom <= currentDate;
}
export function hgsCrossingAvailabilityNoticeFor(tool: ToolDefinition, crossingId: HgsCrossing, currentDate: string) {
    const config = requireConfig(tool, 'hgs-fixed-tariffs-v1');
    const crossing = config.crossings.find((item) => item.id === crossingId);
    if (!crossing)
        return 'Seçili geçiş için doğrulanmış tarife bulunamadı.';
    return crossing.effectiveFrom > currentDate
        ? `${crossing.label} tarifesi ${crossing.effectiveFrom} tarihinde yürürlüğe girecek.`
        : null;
}
export function assertHgsCrossingEffective(tool: ToolDefinition, crossingId: HgsCrossing, currentDate: string) {
    const notice = hgsCrossingAvailabilityNoticeFor(tool, crossingId, currentDate);
    if (notice)
        throw new Error(notice);
}
export function hgsConfigFor(tool: ToolDefinition): HgsConfig {
    const config = requireConfig(tool, 'hgs-fixed-tariffs-v1');
    function classLabel(classId: HgsVehicleClass) {
        const vehicleClass = config.vehicleClasses.find((item) => item.id === classId);
        if (!vehicleClass)
            throw new Error(`HGS tarifesinde ${classId} araç sınıfı eksik.`);
        return vehicleClass.label;
    }
    const classLabels = [
        classLabel('class-1'),
        classLabel('class-2'),
        classLabel('class-3'),
        classLabel('class-4'),
        classLabel('class-5'),
        classLabel('class-6'),
    ];
    return {
        metadata: metadataFor(tool),
        crossings: {
            '15-temmuz': crossingFor(config, '15-temmuz'),
            fsm: crossingFor(config, 'fsm'),
            yss: crossingFor(config, 'yss'),
            osmangazi: crossingFor(config, 'osmangazi'),
            '1915-canakkale': crossingFor(config, '1915-canakkale'),
            avrasya: crossingFor(config, 'avrasya'),
        },
        vehicleClassNames: classLabels,
        roundTripMultiplier: config.roundTripMultiplier,
    };
}
